import React, { useEffect, useState, type ReactNode } from 'react';
import { firebaseAuthService } from '../../lib/firebase/auth';
import type { IdentityUser } from '../../features/user/types/user';
import { type User as FirebaseAuthUser } from 'firebase/auth';
import { AuthContext, type AuthContextType } from './context';

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<IdentityUser | null>(null);
  const [authPhoneNumber, setAuthPhoneNumber] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = firebaseAuthService.onAuthStateChanged(
      (firebaseUser: FirebaseAuthUser | null) => {
        // Firebase user state change - user will be set by useAuth hook after successful signup
        // For now, we'll keep user as null until the signup process completes
        if (!firebaseUser) {
          setUser(null);
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
        setIsLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const signIn = async (user: IdentityUser): Promise<void> => {
    setUser(user);
    setIsAuthenticated(true);
  };

  const logout = async (): Promise<void> => {
    setUser(null);
    setIsAuthenticated(false);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    signIn,
    logout,
    setUser,
    setAuthPhoneNumber,
    authPhoneNumber,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
